import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import React from 'react';

/*****************************************************
 * Locals
 ******************************************************/
import gradientGenerate from '../../util/style-generator/gradient';
import ColorPicker from './ColorPicker/ColorPicker';
import EmptyBoxButton from './EmptyBoxButton';
import Tab, { TabOption } from './reusable/Tab';
import WithLabel from './WithLabel';
import Range from './Range';
import Select from './Select';

/*****************************************************
 * Styles
 ******************************************************/
const Preview = styled.div`
    margin-bottom: 20px;
    border-radius: 5px;
    background: red;
    height: 100px;
`;

const Actions = styled.div`
    text-align: right;
    padding: 10px;
    color: #fff;
`;

const ColorWrap = styled.div`
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 20px;
    display: grid;
    margin-bottom: 20px;
`;

const initialValue = [
    ['type', 'linear'],
    ['direction', '45deg'],
    ['start', ['#FC466B', '0%']],
    ['end', ['#3F5EFB', '100%']],
];

const rgx = /^(start|end)/;

const radialOptions = [
    {
        value: 'center',
        name: 'Center',
    },
    {
        value: 'left',
        name: 'Left',
    },
    {
        value: 'top left',
        name: 'Top Left',
    },
    {
        value: 'top',
        name: 'Top',
    },
    {
        value: 'top right',
        name: 'Top Right',
    },
    {
        value: 'right',
        name: 'Right',
    },
    {
        value: 'bottom right',
        name: 'Bottom Right',
    },
    {
        value: 'bottom',
        name: 'Bottom',
    },
    {
        value: 'bottom left',
        name: 'Bottom Left',
    },
];

const colorPickerOptions = {
    saved: false,
    clear: false,
    previewWidth: '40px',
};

const BackgroundGradient = (props) => {
    const { name: styleProp, onChange } = props;
    const value =
        typeof props.value === 'string' || !props.value ? '' : props.value;

    const gradient = new Map(value);
    const radialDirection = gradient.get('radialDirection');
    const [startColor, startPos] = gradient.get('start') || ['red', '0%'];
    const [endColor, endPos] = gradient.get('end') || ['green', '100%'];
    const direction = gradient.get('direction');
    const shape = gradient.get('shape');
    const type = gradient.get('type');

    const handleChange = (payload) => {
        const { value, name } = payload;

        // checking if the name starts with 'start' or 'end'
        // To construct color and position array
        if (rgx.test(name)) {
            const [key, fraction] = name.split('-');
            const arr = [...gradient.get(key)];
            if (fraction === 'color') {
                arr[0] = value;
            }
            if (fraction === 'pos') {
                arr[1] = value;
            }
            gradient.set(key, arr);
        } else {
            gradient.set(name, value);
        }

        if (!shape && name === 'type' && value === 'radial') {
            gradient.set('shape', 'circle');
            gradient.set('radialDirection', 'center');
        }

        onChange({ name: styleProp, value: Array.from(gradient) });
    };

    const handleChangeType = ({ value }) => {
        handleChange({ value, name: 'type' });
    };

    const handleChangeShape = ({ value }) => {
        handleChange({ value, name: 'shape' });
    };

    const removeGradient = (e) => {
        onChange({ value: '', name: styleProp });
    };

    const enableGradient = () => {
        const gradient = new Map(initialValue);
        onChange({ value: Array.from(gradient), name: styleProp });
    };

    const gradientBackground =
        value &&
        gradientGenerate('backgroundImage', value)
            .replace(';', '')
            .split(':')[1];

    if (!value) {
        return (
            <EmptyBoxButton onClick={enableGradient}>
                Add Custom Gradient
            </EmptyBoxButton>
        );
    }

    return (
        <>
            <Preview style={{ backgroundImage: gradientBackground }}>
                <Actions>
                    <span onClick={removeGradient}>
                        <FontAwesomeIcon icon="trash-alt" />
                    </span>
                </Actions>
            </Preview>

            <ColorWrap>
                <ColorPicker
                    {...colorPickerOptions}
                    onChange={handleChange}
                    label="Start Color"
                    name="start-color"
                    value={startColor}
                    colorVars={false}
                />
                <ColorPicker
                    {...colorPickerOptions}
                    onChange={handleChange}
                    label="End Color"
                    colorVars={false}
                    value={endColor}
                    name="end-color"
                />
            </ColorWrap>

            <Range
                style={{ marginBottom: '15px' }}
                onChange={handleChange}
                labelPosition="inline"
                labelWidth="63px"
                name="start-pos"
                defaultUnit="%"
                value={startPos}
                label="Start"
                max={100}
                min={0}
            />

            <Range
                onChange={handleChange}
                labelPosition="inline"
                labelWidth="63px"
                defaultUnit="%"
                name="end-pos"
                value={endPos}
                label="End"
                max={100}
                min={0}
            />

            <WithLabel label="Gradient Type">
                <Tab selected={type} onSelect={handleChangeType} type="boxed">
                    <TabOption value="linear">Linear Gradient</TabOption>
                    <TabOption value="radial">Redial Gradient</TabOption>
                </Tab>
            </WithLabel>

            {type === 'linear' ? (
                <WithLabel label="Gradient Direction">
                    <Range
                        onChange={handleChange}
                        name="direction"
                        defaultValue="0deg"
                        defaultUnit="deg"
                        value={direction}
                        max={360}
                        min={0}
                    />
                </WithLabel>
            ) : (
                <>
                    <WithLabel label="Radial Shape">
                        <Tab
                            type="boxed"
                            selected={shape}
                            onSelect={handleChangeShape}
                        >
                            <TabOption value="circle">Circle</TabOption>
                            <TabOption value="ellipse">Ellipse</TabOption>
                        </Tab>
                    </WithLabel>
                    <WithLabel label="Radial Direction">
                        <Select
                            options={radialOptions}
                            onChange={handleChange}
                            value={radialDirection}
                            name="radialDirection"
                        />
                    </WithLabel>
                </>
            )}
        </>
    );
};
export default React.memo(BackgroundGradient);
